/**
 * ADO #313 — Curated Java built-in exception classes, shared by:
 *   - ThrowNodeEditor → dropdown of types the student can raise
 *   - MethodDeclThrowsPicker → multi-select on the method/constructor signature
 *   - checkedExceptionValidator → maps a name to its checked/unchecked status
 *   - collectJavaImports → maps a name to its FQCN for auto-import
 *
 * Each entry:
 *   name      Bare class name as the student types it and as it appears
 *             in generated code (`IOException`, `IllegalArgumentException`).
 *   package   Full Java package. `java.lang` entries need NO import; every
 *             other package gets a single dedupe'd `import …;` line.
 *   checked   true → javac requires either a catch or a `throws` declaration
 *             on the enclosing method. false → unchecked (RuntimeException /
 *             Error family), free to throw without ceremony.
 *
 * Phase 3 curated set:
 *   - All 5 Phase 2 unchecked exceptions stay (they already shipped in #304).
 *   - 6 built-in checked exceptions added: the ones a second-semester Java
 *     course actually touches (file I/O, JDBC, threading, parsing).
 *
 * Explicitly NOT in the set:
 *   - `Exception` / `Throwable` themselves — students should pick a specific
 *     subtype; catching `Exception` is an anti-pattern for beginners.
 *   - `Error` and its subclasses (`OutOfMemoryError`, …) — never the right
 *     thing for a student to throw or catch.
 */

export const JAVA_BUILTIN_EXCEPTIONS = [
  // ── Unchecked (RuntimeException family) — Phase 2 set, unchanged ──────
  { name: 'IllegalArgumentException', package: 'java.lang', checked: false },
  { name: 'IllegalStateException',    package: 'java.lang', checked: false },
  { name: 'RuntimeException',         package: 'java.lang', checked: false },
  { name: 'ArithmeticException',      package: 'java.lang', checked: false },
  { name: 'NullPointerException',     package: 'java.lang', checked: false },

  // ── Checked — Phase 3 set ─────────────────────────────────────────────
  { name: 'IOException',              package: 'java.io',    checked: true },
  { name: 'FileNotFoundException',    package: 'java.io',    checked: true },
  { name: 'SQLException',             package: 'java.sql',   checked: true },
  { name: 'ClassNotFoundException',   package: 'java.lang',  checked: true },
  { name: 'InterruptedException',     package: 'java.lang',  checked: true },
  { name: 'ParseException',           package: 'java.text',  checked: true },
];

export const JAVA_UNCHECKED_EXCEPTIONS = JAVA_BUILTIN_EXCEPTIONS
  .filter((e) => !e.checked)
  .map((e) => e.name);

export const JAVA_CHECKED_EXCEPTIONS = JAVA_BUILTIN_EXCEPTIONS
  .filter((e) => e.checked)
  .map((e) => e.name);

/**
 * Look up a built-in by bare name. Returns the catalog entry or null.
 * Names are matched verbatim — custom / project-defined exception classes
 * are deliberately not in the catalog (their checked status is inferred at
 * call sites from the project's Configuration.extendsClass).
 */
export function findBuiltinException(name) {
  if (!name) return null;
  return JAVA_BUILTIN_EXCEPTIONS.find((e) => e.name === name) || null;
}

/**
 * True if the name is a known built-in checked exception. False for
 * unchecked, unknown, or project-defined names (the latter are checked
 * via the heuristic in checkedExceptionValidator that walks Configuration.extendsClass).
 */
export function isBuiltinCheckedException(name) {
  const e = findBuiltinException(name);
  return !!(e && e.checked);
}

/**
 * Return the FQCN (`java.io.IOException`) for the given bare name if it's a
 * built-in AND lives outside java.lang. Returns null for java.lang entries
 * (no import needed) and for unknown names (the caller should treat those
 * as project-local or Custom and skip the import-set add).
 */
export function importFqcnForBuiltinException(name) {
  const e = findBuiltinException(name);
  if (!e) return null;
  if (e.package === 'java.lang') return null;
  return `${e.package}.${e.name}`;
}
